'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, Calendar, Check, X, Home, Hourglass, CheckCircle2, XCircle, ListChecks, AlertTriangle } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { workingDaysBetween } from '@/lib/date-utils'
import {
  WFH_MONTHLY_LIMIT,
  currentMonthKey,
  monthKey,
  formatMonthKey,
  monthlyUsageIndex,
  usageFrom,
  workingDaysInMonth,
  isOverWfhLimit,
  isPending,
  wfhLimitWarningFor,
} from '@/lib/allowance'
import { AllowanceWarning } from '@/components/ui/allowance-warning'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { APPROVE_BTN, REJECT_BTN } from '@/components/ui/button-styles'

interface User {
  id: string
  name: string
  employeeId: string | null
  department: string | null
}

interface WFHRequest {
  id: string
  userId: string | null
  startDate: string
  endDate: string
  reason: string
  status: string
  rejectionReason: string | null
  createdAt: string
  user: User
}

/**
 * Flags a request whose employee is over — or would be pushed over by this
 * approval — the 4-day monthly WFH allowance. Renders nothing otherwise.
 */
function OverLimitPill({ wfh, allowance }: { wfh: WFHRequest; allowance: { month: string; total: number; pending: boolean; over: boolean } }) {
  if (!allowance.over) return null
  const label = allowance.pending ? 'Would exceed WFH limit' : 'WFH limit exceeded'
  return (
    <StatusPill
      tone="red"
      className="mt-1"
      title={`${wfhLimitWarningFor(wfh.user?.name)} (${allowance.total} of ${WFH_MONTHLY_LIMIT} days in ${formatMonthKey(allowance.month)})`}
    >
      <AlertTriangle className="h-3 w-3" />
      {label} · {allowance.total}/{WFH_MONTHLY_LIMIT}
    </StatusPill>
  )
}

export function WFHManagement() {
  const [wfhRequests, setWfhRequests] = useState<WFHRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [actionDialog, setActionDialog] = useState<{ open: boolean; wfh: WFHRequest | null; action: 'APPROVED' | 'REJECTED' | null }>({ open: false, wfh: null, action: null })
  const [rejectionReason, setRejectionReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/wfh${filterStatus && filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`)
      const data = await res.json()
      if (data.wfhRequests) setWfhRequests(data.wfhRequests)
    } catch (error) {
      console.error('Error fetching WFH requests:', error)
      toast.error('Failed to fetch WFH requests')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Keyed on mount (not [wfhRequests]) so filter changes don't refetch the whole
  // collection; mutations refetch it explicitly via fetchAllWfh().
  const [allWfh, setAllWfh] = useState<WFHRequest[]>([])
  const fetchAllWfh = useCallback(() => {
    apiFetch('/api/wfh').then(r => r.json()).then(d => {
      if (d.wfhRequests) setAllWfh(d.wfhRequests)
    }).catch(() => {})
  }, [])
  useEffect(() => {
    fetchAllWfh()
  }, [fetchAllWfh])

  const kpis = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    const inMonth = (iso: string) => {
      const d = new Date(iso + 'T00:00:00')
      return d.getFullYear() === y && d.getMonth() === m
    }
    const pending = allWfh.filter(l => l.status === 'PENDING').length
    const approvedM = allWfh.filter(l => l.status === 'APPROVED' && inMonth(l.startDate)).length
    const rejected = allWfh.filter(l => l.status === 'REJECTED').length
    const totalM = allWfh.filter(l => inMonth(l.startDate)).length
    return { pending, approvedM, rejected, totalM }
  }, [allWfh])

  // Approved WFH days per employee per month, from the unfiltered list — so the
  // allowance is judged against every request, not just the ones on screen.
  const usageIndex = useMemo(
    () => monthlyUsageIndex(allWfh.map(w => ({ ...w, userId: w.userId ?? w.user?.id ?? null }))),
    [allWfh]
  )

  /**
   * Where this request leaves the employee's 4-day month. A pending request
   * counts its own days too — approving it is what would breach the allowance,
   * which is exactly what the approver needs to see before clicking.
   */
  const allowance = useCallback((wfh: WFHRequest) => {
    const month = monthKey(wfh.startDate)
    const userId = wfh.userId ?? wfh.user?.id ?? null
    const used = usageFrom(usageIndex, userId, month)
    // Only a pending request adds to the projection — approved days are already
    // in `used`, and rejected days were never taken.
    const pending = isPending(wfh)
    const total = used + (pending ? workingDaysInMonth(wfh.startDate, wfh.endDate, month) : 0)
    return { month, total, pending, over: isOverWfhLimit(total) }
  }, [usageIndex])

  // How many employees are already over their 4 days this calendar month.
  const overLimitEmployees = useMemo(() => {
    const suffix = `|${currentMonthKey()}`
    return Object.entries(usageIndex).filter(([key, days]) => key.endsWith(suffix) && isOverWfhLimit(days)).length
  }, [usageIndex])

  const handleAction = async () => {
    if (!actionDialog.wfh || !actionDialog.action) return

    setSubmitting(true)
    try {
      await apiFetch(`/api/wfh/${actionDialog.wfh.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: actionDialog.action,
          rejectionReason: actionDialog.action === 'REJECTED' ? rejectionReason : undefined
        })
      })

      toast.success(`WFH request ${actionDialog.action.toLowerCase()}`)
      fetchData()
      fetchAllWfh()
      setActionDialog({ open: false, wfh: null, action: null })
      setRejectionReason('')
    } catch (e: any) {
      toast.error(e.message || 'Failed to update WFH request')
    } finally {
      setSubmitting(false)
    }
  }

  const getDaysCount = (start: string, end: string) => workingDaysBetween(start, end)

  const fmtDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00')
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  }

  return (
    <PageShell>
      <SectionHeader
        icon={Home}
        title="Work From Home Requests"
        description="Approve or reject WFH requests"
        action={
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiTile label="Pending" value={kpis.pending} icon={Hourglass} tone="amber" />
        <KpiTile label="Approved (month)" value={kpis.approvedM} icon={CheckCircle2} tone="green" />
        <KpiTile label="Rejected" value={kpis.rejected} icon={XCircle} tone="red" />
        <KpiTile label="Total (month)" value={kpis.totalM} icon={ListChecks} tone="violet" />
        <KpiTile
          label="Over limit"
          value={overLimitEmployees}
          icon={AlertTriangle}
          tone="red"
          hint={`Employees past ${WFH_MONTHLY_LIMIT} days this month`}
        />
      </div>

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : wfhRequests.length === 0 ? (
          <EmptyState
            icon={Home}
            title="No WFH requests to display"
            description="Try a different status filter — new requests appear here as employees submit them."
          />
        ) : (
          <>
            <div className="hidden md:block">
              <ScrollArea className="h-[520px]">
                <div className="min-w-[760px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-500 dark:text-white/50">Employee</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Duration</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Reason</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wfhRequests.map((wfh) => (
                        <TableRow key={wfh.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:bg-white/5">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                                <Home className="h-4 w-4 text-violet-600 dark:text-violet-300" />
                              </div>
                              <div>
                                <p className="text-neutral-900 dark:text-white/90 font-medium">{wfh.user?.name || 'Unknown User'}</p>
                                <p className="text-xs text-neutral-400 dark:text-white/40">{wfh.user?.department || wfh.user?.employeeId || '-'}</p>
                                <OverLimitPill wfh={wfh} allowance={allowance(wfh)} />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-neutral-800 dark:text-white/80">
                              <div className="flex items-center gap-1.5 text-sm">
                                <Calendar className="h-3.5 w-3.5 text-neutral-400 dark:text-white/40" />
                                {fmtDate(wfh.startDate)} – {fmtDate(wfh.endDate)}
                              </div>
                              <p className="text-xs text-neutral-400 dark:text-white/40 mt-0.5">{getDaysCount(wfh.startDate, wfh.endDate)} day(s)</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-neutral-700 dark:text-white/70 max-w-[220px]">
                            <p className="truncate">{wfh.reason}</p>
                          </TableCell>
                          <TableCell>
                            <StatusPill tone={statusTone(wfh.status)} dot>{wfh.status}</StatusPill>
                          </TableCell>
                          <TableCell className="text-right">
                            {wfh.status === 'PENDING' ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10"
                                  onClick={() => setActionDialog({ open: true, wfh, action: 'APPROVED' })}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10"
                                  onClick={() => setActionDialog({ open: true, wfh, action: 'REJECTED' })}
                                >
                                  <X className="h-4 w-4 mr-1" /> Reject
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-neutral-400 dark:text-white/40">
                                {wfh.rejectionReason && `Rejected: ${wfh.rejectionReason}`}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </div>

            <div className="md:hidden space-y-3">
              {wfhRequests.map((wfh) => (
                <GlassCard key={wfh.id} tone="violet">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Home className="h-4 w-4 text-violet-600 dark:text-violet-300 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-neutral-900 dark:text-white/90 font-medium truncate">{wfh.user?.name || 'Unknown'}</p>
                        <p className="text-xs text-neutral-400 dark:text-white/40">{wfh.user?.department || wfh.user?.employeeId || '-'}</p>
                        <OverLimitPill wfh={wfh} allowance={allowance(wfh)} />
                      </div>
                    </div>
                    <StatusPill tone={statusTone(wfh.status)} dot>{wfh.status}</StatusPill>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-neutral-600 dark:text-white/60 inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {fmtDate(wfh.startDate)} – {fmtDate(wfh.endDate)}
                    </span>
                    <span className="text-xs text-neutral-400 dark:text-white/40">{getDaysCount(wfh.startDate, wfh.endDate)} day(s)</span>
                  </div>
                  {wfh.reason && <p className="mt-2 text-sm text-neutral-700 dark:text-white/70 line-clamp-2">{wfh.reason}</p>}
                  {wfh.status === 'PENDING' ? (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" className="flex-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25" onClick={() => setActionDialog({ open: true, wfh, action: 'APPROVED' })}>
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" className="flex-1 bg-rose-500/15 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25" onClick={() => setActionDialog({ open: true, wfh, action: 'REJECTED' })}>
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  ) : wfh.rejectionReason ? (
                    <p className="mt-2 text-xs text-neutral-400 dark:text-white/40">Rejected: {wfh.rejectionReason}</p>
                  ) : null}
                </GlassCard>
              ))}
            </div>
          </>
        )}
      </GlassCard>

      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent className="bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">
              {actionDialog.action === 'APPROVED' ? 'Approve WFH Request' : 'Reject WFH Request'}
            </DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              {actionDialog.action === 'APPROVED'
                ? 'Are you sure you want to approve this WFH request?'
                : 'Please provide a reason for rejecting this WFH request.'}
            </DialogDescription>
          </DialogHeader>
          {actionDialog.action === 'APPROVED' && actionDialog.wfh && allowance(actionDialog.wfh).over && (
            <AllowanceWarning message={wfhLimitWarningFor(actionDialog.wfh.user?.name)} />
          )}
          {actionDialog.action === 'REJECTED' && (
            <div className="space-y-2">
              <label className="text-sm text-neutral-700 dark:text-white/70">Rejection Reason</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, wfh: null, action: null })} className="border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white/70">
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={submitting || (actionDialog.action === 'REJECTED' && !rejectionReason)}
              className={actionDialog.action === 'APPROVED' ? APPROVE_BTN : REJECT_BTN}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                actionDialog.action === 'APPROVED' ? 'Approve' : 'Reject'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  )
}
